import styled, {css} from "styled-components";
import {halloween, halloweenVampire, halloweenWitch, halloweenZombie, radii} from "theme/tokens";
import type {TakeoverTemplateKey} from "sponsor/sponsorBooking";

/**
 * Externalized styles for {@link ./TemplateTilePicker} (kitecorp/transferify-ui#1953).
 *
 * The tile is the same native radio group the sponsor's card picker is — a `<label>` around a visually
 * hidden `<input type="radio">`, so arrow-key navigation, mutual exclusivity and the screen-reader
 * announcement are the browser's rather than three hand-rolled key handlers. Only the DENSITY differs,
 * and it differs completely: a glyph and a name, no live renderer, no description, no arity chip and no
 * "see bigger" launcher. The hidden input itself is imported from the card picker rather than restated,
 * because the visually-hidden recipe is one decision.
 */

/** One labelled group of tiles — a category, the way every other picker in the app is laid out. */
export const TileGroup = styled.div`
	& + & {
		margin-top: 14px;
	}
`;

/**
 * The group's own name, quieter than the section label above it.
 *
 * A group heading rather than a sentence: the builder's sections carry a single uppercase label and a
 * chip now, and a second full-size label inside one would read as a new section.
 */
export const GroupLabel = styled.div`
	margin-bottom: 6px;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.05em;
	text-transform: uppercase;
	color: ${({theme}) => theme.tokens.ink3};
`;

/**
 * Two tiles per row, at EVERY width.
 *
 * The prototype drew three; the owner asked for two on the first review of the built sheet
 * (2026-09-03, "just to make it easier to scroll"): a wider tile is a bigger target under a thumb and
 * a shorter list to read, and it also seats the longest Romanian names ("Bandă în mișcare", "Coperți în
 * șir") on one line. Deliberately a fixed `repeat(2, 1fr)` rather than the card picker's `auto-fit`
 * reflow: the reflow the cards need exists because a card carries a live 16:10 preview stage and a
 * paragraph, and neither survives being half of a phone. Two grids, two densities, two rules, which is
 * why this is its own component.
 */
export const TileGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 8px;
`;

/** The tile. A `<label>`, so clicking anywhere in it activates the radio it wraps. */
export const Tile = styled.label<{$selected: boolean; $disabled: boolean}>`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 8px;
	box-sizing: border-box;
	border-radius: ${radii.control};
	border: 1.5px solid ${({theme, $selected}) => ($selected ? theme.tokens.selectedEdge : theme.tokens.fieldLine)};
	background: ${({theme}) => theme.tokens.card};
	/* The selected RING (owner-approved prototype): the border alone reads as a hover on a 1.5px edge. */
	box-shadow: ${({theme, $selected}) => ($selected ? `0 0 0 2px ${theme.tokens.ring}` : "none")};
	cursor: ${({$disabled}) => ($disabled ? "not-allowed" : "pointer")};
	opacity: ${({$disabled}) => ($disabled ? 0.55 : 1)};
	transition: border-color 0.16s, box-shadow 0.16s;

	&:hover {
		border-color: ${({theme, $selected, $disabled}) =>
		($disabled && !$selected ? theme.tokens.fieldLine : theme.tokens.selectedEdge)};
	}

	/* The hidden input takes the focus; the ring has to be drawn by the tile the user can see. */
	&:has(input:focus-visible) {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/** The treatment's name, the tile's whole caption. Its description lives in the tile's `title`. */
export const TileName = styled.span`
	font-size: 12px;
	font-weight: 700;
	line-height: 1.2;
	letter-spacing: -0.01em;
	color: ${({theme}) => theme.tokens.ink};
	/* Wraps rather than ellipsises, should a name ever outgrow its tile: a truncated name is not a name
	   at all, and the grid stretches every tile in a row to the tallest, so a two-line name would cost
	   its row a line of height and nothing in alignment. At two per row every catalogue name fits. */
	overflow-wrap: anywhere;
`;

/**
 * How tall the glyph is, and how wide its composition is drawn to be.
 *
 * The marks below are placed in pixels from the glyph's CENTRE rather than from its left edge, so the
 * composition stays centred at any tile width — a third of a 1100px sheet's 380px control column and a
 * third of a 375px phone differ by only a few pixels, but "a few pixels" is the difference between a
 * centred deck and one hugging an edge, and the tile is 105px wide at neither exactly.
 */
const GLYPH_HEIGHT_PX = 44;

/**
 * One mark in the composition: a plate, a bar or a slat, depending on the treatment.
 *
 * Its colour is `blueInk` — the ink token the palette already guarantees is readable ON the blue the
 * stage is painted with. That is the whole reason it is not a `#fff` literal: `blue` lightens in dark
 * mode, so white marks would sit at about 1.4:1 there, and `blueInk` inverts with it by construction.
 */
export const GlyphMark = styled.i`
	position: absolute;
	border-radius: 3px;
	background: ${({theme}) => theme.tokens.blueInk};
	opacity: 0.78;
`;

/**
 * The per-treatment composition — a tiny STATIC picture of what the animation does.
 *
 * Static on purpose, and this is the decision the ticket turns on. The card picker mounts the REAL
 * renderer, which is right when a card is 220px wide and the motion is the product being sold; at 105px
 * with nine of them on screen it is nine simultaneous animation loops for a control the sender operates
 * in a second, and the live miniature beside the builder already shows the chosen treatment moving. So
 * the tile shows the SHAPE — a fanned deck, a row of bars, a receding cascade — and the miniature shows
 * the motion.
 *
 * Total over the whole treatment union rather than over the page-theme subset the builder offers today,
 * so an eleventh treatment is a compile error here instead of an empty box on screen.
 */
/**
 * A scene's tile: its own sky in both page modes over the catalogue's blue, its moon, its near hill, and the one
 * thing the scene is about standing on that hill (`subject`, the third mark).
 */
function sceneGlyph(zenith: string, dusk: string, moon: string, subject: ReturnType<typeof css>) {
	return css`
		background: linear-gradient(180deg, ${zenith}, ${dusk});
		${GlyphMark}:nth-child(1) { left: calc(50% + 10px); top: 6px; width: 12px; height: 12px; border-radius: 50%; background: ${moon}; opacity: 1; }
		${GlyphMark}:nth-child(2) { left: -10%; top: 30px; width: 120%; height: 30px; border-radius: 50%; background: ${halloween.bat}; opacity: 1; }
		${GlyphMark}:nth-child(3) { opacity: 1; ${subject} }
	`;
}

const GLYPH_MARKS: Record<TakeoverTemplateKey, ReturnType<typeof css>> = {
	// A fanned deck: three plates, the middle one square on and the outer two splayed.
	CAROUSEL: css`
		${GlyphMark} {
			width: 22px;
			height: 30px;
			top: 7px;
		}
		${GlyphMark}:nth-child(1) { left: calc(50% - 29px); transform: rotate(-8deg); }
		${GlyphMark}:nth-child(2) { left: calc(50% - 11px); top: 5px; }
		${GlyphMark}:nth-child(3) { left: calc(50% + 7px); transform: rotate(8deg); }
	`,
	// A band of equal plates drifting sideways.
	SCROLLING_BARS: css`
		${GlyphMark} {
			width: 18px;
			height: 20px;
			top: 12px;
		}
		${GlyphMark}:nth-child(1) { left: calc(50% - 42px); }
		${GlyphMark}:nth-child(2) { left: calc(50% - 20px); }
		${GlyphMark}:nth-child(3) { left: calc(50% + 2px); }
		${GlyphMark}:nth-child(4) { left: calc(50% + 24px); }
	`,
	// One plate filling the frame — the treatment that shows a single picture and settles it.
	HERO_SCROLL: css`
		${GlyphMark}:nth-child(1) {
			left: 14px;
			right: 14px;
			top: 8px;
			bottom: 8px;
			border-radius: 5px;
		}
	`,
	// A receding cascade: each plate lower and fainter than the one before it.
	PLANES: css`
		${GlyphMark} {
			width: 18px;
			height: 24px;
		}
		${GlyphMark}:nth-child(1) { left: calc(50% - 31px); top: 4px; }
		${GlyphMark}:nth-child(2) { left: calc(50% - 9px); top: 10px; opacity: 0.62; }
		${GlyphMark}:nth-child(3) { left: calc(50% + 13px); top: 16px; opacity: 0.46; }
	`,
	// A ring seen edge-on: the outer slats foreshortened as they turn away.
	RING: css`
		${GlyphMark} {
			width: 14px;
			height: 22px;
			top: 11px;
			border-radius: 2px;
		}
		${GlyphMark}:nth-child(1) { left: calc(50% - 41px); transform: scaleX(0.5); }
		${GlyphMark}:nth-child(2) { left: calc(50% - 19px); }
		${GlyphMark}:nth-child(3) { left: calc(50% + 5px); }
		${GlyphMark}:nth-child(4) { left: calc(50% + 27px); transform: scaleX(0.5); }
	`,
	// Cover flow: the outer plates hinged away in perspective around a square-on centre.
	COVERFLOW: css`
		${GlyphMark} {
			width: 16px;
			height: 26px;
			top: 9px;
		}
		${GlyphMark}:nth-child(1) { left: calc(50% - 36px); transform: perspective(40px) rotateY(40deg); }
		${GlyphMark}:nth-child(2) { left: calc(50% - 10px); height: 30px; top: 7px; }
		${GlyphMark}:nth-child(3) { left: calc(50% + 20px); transform: perspective(40px) rotateY(-40deg); }
	`,
	// The curtain: a full-height panel drawn across the page, with the wipe's own hard edge on it.
	WIPE_REVEAL: css`
		${GlyphMark}:nth-child(1) {
			left: 0;
			top: 0;
			bottom: 0;
			width: 46%;
			border-radius: 0;
		}
		${GlyphMark}:nth-child(2) {
			left: 46%;
			top: 0;
			bottom: 0;
			width: 3px;
			border-radius: 0;
			opacity: 1;
		}
	`,
	// Two plates mid-dissolve: one arriving, one on its way out.
	CROSSFADE: css`
		${GlyphMark}:nth-child(1) {
			left: 8px;
			right: 26px;
			top: 6px;
			bottom: 6px;
			border-radius: 4px;
		}
		${GlyphMark}:nth-child(2) {
			left: 26px;
			right: 8px;
			top: 6px;
			bottom: 6px;
			border-radius: 4px;
			opacity: 0.4;
		}
	`,
	// A slow pan: one wide plate running off the frame, with the ghost of where it came from behind it.
	SLOW_DRIFT: css`
		${GlyphMark}:nth-child(1) {
			left: -14px;
			width: 26px;
			top: 8px;
			bottom: 8px;
			border-radius: 4px;
			opacity: 0.38;
		}
		${GlyphMark}:nth-child(2) {
			left: 18px;
			right: -14px;
			top: 8px;
			bottom: 8px;
			border-radius: 4px;
		}
	`,
	/*
	 * A play triangle, and nothing behind it.
	 *
	 * Alone rather than over a plate because the marks are one colour: a light triangle on a light plate
	 * would be invisible, and a second colour here would be a seventh hue in a palette that has six.
	 * Not reachable from the transfer builder — `PAGE_THEME_TEMPLATES` excludes the video treatment
	 * because a transfer stores no video — and defined anyway so the map stays total.
	 */
	VIDEO_LOOP: css`
		${GlyphMark}:nth-child(1) {
			left: calc(50% - 8px);
			top: calc(50% - 11px);
			width: 20px;
			height: 22px;
			border-radius: 2px;
			clip-path: polygon(0 0, 100% 50%, 0 100%);
			opacity: 0.9;
		}
	`,
	/*
	 * The Halloween scenes (kitecorp/transferify-ui#3776): a night stage in the scene's own pigments rather
	 * than the catalogue's blue, because a scene is drawn by this bundle and sells no picture of the
	 * sender's. Each is a moon, a hill and what the scene is about: a lit pumpkin, a castle tower against the
	 * red moon, the zombie's glowing slime, the witches' bubbling brew.
	 */
	PUMPKIN_NIGHT: sceneGlyph(halloween.night, halloween.dusk, halloween.moon, css`
		left: calc(50% - 11px); top: 22px; width: 22px; height: 16px; border-radius: 45%; background: ${halloween.pumpkin};
	`),
	VAMPIRE: sceneGlyph(halloweenVampire.zenith, halloweenVampire.dusk, halloweenVampire.moonlight, css`
		left: calc(50% + 10px); top: 10px; width: 7px; height: 24px; border-radius: 1px; background: ${halloweenVampire.castle};
	`),
	ZOMBIE: sceneGlyph(halloweenZombie.zenith, halloweenZombie.dusk, halloweenZombie.moonlight, css`
		left: calc(50% - 12px); top: 27px; width: 24px; height: 7px; border-radius: 50%; background: ${halloweenZombie.glow};
		box-shadow: 0 0 8px 2px ${halloweenZombie.glow};
	`),
	WITCH: sceneGlyph(halloweenWitch.zenith, halloweenWitch.dusk, halloweenWitch.moon, css`
		left: calc(50% - 10px); top: 25px; width: 20px; height: 6px; border-radius: 50%; background: ${halloweenWitch.brew};
		box-shadow: 0 0 8px 2px ${halloweenWitch.brew};
	`),
};

/** How many marks each composition is drawn from — read by the view, which renders exactly that many. */
export const GLYPH_MARK_COUNT: Record<TakeoverTemplateKey, number> = {
	CAROUSEL: 3,
	SCROLLING_BARS: 4,
	HERO_SCROLL: 1,
	PLANES: 3,
	RING: 4,
	COVERFLOW: 3,
	WIPE_REVEAL: 2,
	CROSSFADE: 2,
	SLOW_DRIFT: 2,
	VIDEO_LOOP: 1,
	PUMPKIN_NIGHT: 3,
	VAMPIRE: 3,
	ZOMBIE: 3,
	WITCH: 3,
};

/**
 * The glyph's stage: a saturated token gradient with the composition laid over it.
 *
 * `blue → violet` rather than a pale wash, because the marks are `blueInk` and the pair has to keep its
 * contrast in both modes — the palette already guarantees that ink against that blue. The "no
 * animation" tile drops the gradient for the flat field colour, which is the plain page it stands for.
 */
export const TileGlyph = styled.div<{$template: TakeoverTemplateKey | null}>`
	position: relative;
	height: ${GLYPH_HEIGHT_PX}px;
	overflow: hidden;
	border-radius: ${radii.chip};
	background: ${({theme, $template}) => ($template === null
		? theme.tokens.field
		: `linear-gradient(135deg, ${theme.tokens.blue}, ${theme.tokens.violet})`)};
	${({$template}) => ($template === null ? null : GLYPH_MARKS[$template])};
`;
